import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { Customer, TablesInsert, TablesUpdate } from '@t90/common/supabase';
import { useSupabase, queryKeys } from '../provider';

// =====================================================
// CUSTOMER QUERIES
// =====================================================

interface CustomerFilters {
  locationId?: string;
  active?: boolean;
  qbSyncStatus?: string;
}

export function useCustomers(filters?: CustomerFilters) {
  const supabase = useSupabase();

  return useQuery({
    queryKey: queryKeys.customers.list(filters),
    queryFn: async () => {
      let query = supabase
        .from('customers')
        .select(`
          *,
          location:locations(id, location_name, location_code)
        `)
        .order('customer_name');

      if (filters?.locationId) {
        query = query.eq('location_id', filters.locationId);
      }
      if (filters?.active !== undefined) {
        query = query.eq('active', filters.active);
      }
      if (filters?.qbSyncStatus) {
        query = query.eq('qb_sync_status', filters.qbSyncStatus);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data;
    },
  });
}

export function useCustomer(customerId: string | undefined) {
  const supabase = useSupabase();

  return useQuery({
    queryKey: queryKeys.customers.detail(customerId ?? ''),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('customers')
        .select(`
          *,
          location:locations(*),
          orders(
            id,
            order_number,
            order_status,
            order_date,
            due_date,
            priority
          )
        `)
        .eq('id', customerId!)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!customerId && customerId !== 'new',
  });
}

// =====================================================
// CUSTOMER MUTATIONS
// =====================================================

type CustomerInsert = TablesInsert<'customers'>;
type CustomerUpdate = TablesUpdate<'customers'>;

export function useCreateCustomer() {
  const supabase = useSupabase();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (customer: CustomerInsert) => {
      const { data, error } = await supabase
        .from('customers')
        .insert(customer as never)
        .select()
        .single();

      if (error) throw error;
      return data as Customer;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
      toast.success(`Customer "${data.customer_name}" created`);
    },
    onError: (error) => {
      toast.error(`Failed to create customer: ${error.message}`);
    },
  });
}

export function useUpdateCustomer() {
  const supabase = useSupabase();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: CustomerUpdate & { id: string }) => {
      const { data, error } = await supabase
        .from('customers')
        .update(updates as never)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as Customer;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.detail(data.id) });
      toast.success(`Customer "${data.customer_name}" updated`);
    },
    onError: (error) => {
      toast.error(`Failed to update customer: ${error.message}`);
    },
  });
}

export function useDeactivateCustomer() {
  const supabase = useSupabase();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (customerId: string) => {
      const { data, error } = await supabase
        .from('customers')
        .update({ active: false } as never)
        .eq('id', customerId)
        .select()
        .single();

      if (error) throw error;
      return data as Customer;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.detail(data.id) });
      toast.success(`Customer "${data.customer_name}" deactivated`);
    },
    onError: (error) => {
      toast.error(`Failed to deactivate customer: ${error.message}`);
    },
  });
}
